/**
 * PSV-1729 — zero-asset Web Audio SFX synthesizer (Starblast sound pack).
 *
 * No audio files, no Phaser: Phaser's sound system wants loaded assets,
 * which means shipping/licensing CC0 packs or pulling from a CDN. Instead
 * every effect is synthesized jsfxr-style at call time from OscillatorNode
 * tones and a shared white-noise AudioBuffer, routed through per-effect
 * envelope GainNodes into one master GainNode (0.5). Mute keeps the
 * AudioContext running and drops the master gain to 0 (plus an early-out
 * so muted play calls schedule nothing).
 *
 * Autoplay policy: the AudioContext is created lazily on the first play
 * call, and `resume()` should be wired to a user gesture (pointerdown /
 * keydown) — it resumes a suspended context.
 *
 * Test-safety: the constructor never touches `window`, and every method
 * silently no-ops when Web Audio is unavailable (jsdom/vitest) or the
 * lazy init failed. Envelope tails never exponential-ramp to exactly 0
 * (invalid target) — they ramp to 0.0001 and then setValueAtTime(0).
 *
 * Rate-limit: each effect keeps a lastPlayedAt timestamp and skips
 * retriggers within 45ms, so 10Hz fire volleys from multiple ships don't
 * clip into a wall of sound.
 */

/** Minimum interval between retriggers of the same effect (ms). */
const MIN_RETRIGGER_MS = 45;

/** Master output level (pre-mute). */
const MASTER_GAIN = 0.5;

/** Length of the shared white-noise buffer (seconds). */
const NOISE_BUFFER_SECONDS = 1;

/** Floor used instead of 0 for exponential ramps (0 is invalid). */
const ENV_FLOOR = 0.0001;

/** Short linear attack applied to every envelope (seconds). */
const ATTACK_S = 0.005;

interface ToneOpts {
  type: OscillatorType;
  /** Start frequency (Hz). */
  freq: number;
  /** Optional end frequency for an exponential sweep (Hz). */
  freqEnd?: number;
  /** Start time (AudioContext time, seconds). */
  at: number;
  /** Duration (seconds). */
  dur: number;
  /** Peak envelope gain. */
  peak: number;
}

interface NoiseOpts {
  filterType: BiquadFilterType;
  /** Filter cutoff/center frequency (Hz). */
  freq: number;
  /** Optional end frequency for an exponential filter sweep (Hz). */
  freqEnd?: number;
  /** Filter Q (bandpass character). */
  q?: number;
  /** Start time (AudioContext time, seconds). */
  at: number;
  /** Duration (seconds). */
  dur: number;
  /** Peak envelope gain. */
  peak: number;
}

export class SoundManager {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;

  /** True once `new AudioContext` threw or Web Audio is absent — stop retrying. */
  private initFailed = false;
  private closed = false;
  private muted = false;

  /** Per-effect last trigger time (ms epoch) for the 45ms rate limit. */
  private readonly lastPlayedAt: Record<string, number> = Object.create(null) as Record<
    string,
    number
  >;

  /**
   * Resume a suspended AudioContext — call from a user-gesture handler
   * (pointerdown/keydown) to satisfy browser autoplay policy.
   */
  resume(): void {
    const ctx = this.ensureContext();
    if (ctx !== null && ctx.state === 'suspended') {
      void ctx.resume();
    }
  }

  // ── Effects ────────────────────────────────────────────────────────

  /** Weapon shot: short 60ms square blip sweeping 880→440Hz. */
  fire(): void {
    const ctx = this.begin('fire');
    if (ctx === null) return;
    this.tone(ctx, {
      type: 'square',
      freq: 880,
      freqEnd: 440,
      at: ctx.currentTime,
      dur: 0.06,
      peak: 0.12,
    });
  }

  /** Projectile impact: 80ms bright white-noise burst, fast decay. */
  hit(): void {
    const ctx = this.begin('hit');
    if (ctx === null) return;
    this.noise(ctx, {
      filterType: 'highpass',
      freq: 1800,
      at: ctx.currentTime,
      dur: 0.08,
      peak: 0.15,
    });
  }

  /** Ship destroyed: 450ms low noise boom + sub-oscillator thump. */
  shipExplosion(): void {
    if (this.begin('shipExplosion') === null) return;
    this.explosion(0.45);
  }

  /** Death (own ship): shipExplosion variant with a longer 700ms tail. */
  death(): void {
    if (this.begin('death') === null) return;
    this.explosion(0.7);
  }

  /** Asteroid chunk broken off: 150ms mid-band filtered noise crackle. */
  asteroidCrack(): void {
    const ctx = this.begin('asteroidCrack');
    if (ctx === null) return;
    this.noise(ctx, {
      filterType: 'bandpass',
      freq: 1200,
      q: 1.2,
      at: ctx.currentTime,
      dur: 0.15,
      peak: 0.18,
    });
  }

  /** Crystal collected: bright pleasant E6→B6 sine two-note blip. */
  crystalPickup(): void {
    const ctx = this.begin('crystalPickup');
    if (ctx === null) return;
    const t = ctx.currentTime;
    this.tone(ctx, { type: 'sine', freq: 1318.51, at: t, dur: 0.06, peak: 0.15 }); // E6
    this.tone(ctx, { type: 'sine', freq: 1975.53, at: t + 0.06, dur: 0.06, peak: 0.15 }); // B6
  }

  /** Stat upgrade bought: rising three-note sine arpeggio (~200ms). */
  upgrade(): void {
    const ctx = this.begin('upgrade');
    if (ctx === null) return;
    const t = ctx.currentTime;
    const notes = [440, 554.37, 659.25]; // A4 → C#5 → E5 (A-major triad)
    for (let i = 0; i < notes.length; i++) {
      const freq = notes[i];
      if (freq === undefined) continue;
      this.tone(ctx, { type: 'sine', freq, at: t + i * 0.066, dur: 0.07, peak: 0.18 });
    }
  }

  /**
   * PSV-1886 — kill-streak stinger: two heavy low square hits with a
   * fifth on top (D3→A3), more menace than celebration.
   */
  streak(): void {
    const ctx = this.begin('streak');
    if (ctx === null) return;
    const t = ctx.currentTime;
    this.tone(ctx, { type: 'square', freq: 146.83, at: t, dur: 0.16, peak: 0.2 });
    this.tone(ctx, { type: 'square', freq: 220, at: t + 0.14, dur: 0.22, peak: 0.2 });
    this.tone(ctx, { type: 'sine', freq: 440, at: t + 0.14, dur: 0.22, peak: 0.1 });
  }

  /** Ship tier up: triumphant four-note square+sine fanfare (~500ms). */
  tierUp(): void {
    const ctx = this.begin('tierUp');
    if (ctx === null) return;
    const t = ctx.currentTime;
    // C5 → E5 → G5 → C6, last note held; square carries the brass edge,
    // a sine layer an octave down fills out the body.
    const notes: ReadonlyArray<readonly [freq: number, at: number, dur: number]> = [
      [523.25, 0, 0.1],
      [659.25, 0.1, 0.1],
      [783.99, 0.2, 0.1],
      [1046.5, 0.3, 0.2],
    ];
    for (const [freq, offset, dur] of notes) {
      this.tone(ctx, { type: 'square', freq, at: t + offset, dur, peak: 0.12 });
      this.tone(ctx, { type: 'sine', freq: freq / 2, at: t + offset, dur, peak: 0.1 });
    }
  }

  /** Hull scraping an asteroid/ship: 120ms low noise rumble. */
  contactScrape(): void {
    const ctx = this.begin('contactScrape');
    if (ctx === null) return;
    this.noise(ctx, {
      filterType: 'lowpass',
      freq: 300,
      at: ctx.currentTime,
      dur: 0.12,
      peak: 0.15,
    });
  }

  // ── Mute / lifecycle ───────────────────────────────────────────────

  setMuted(m: boolean): void {
    this.muted = m;
    if (this.master !== null && this.ctx !== null) {
      this.master.gain.setValueAtTime(m ? 0 : MASTER_GAIN, this.ctx.currentTime);
    }
  }

  /** Flip mute; returns the NEW muted state. */
  toggleMuted(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  isMuted(): boolean {
    return this.muted;
  }

  /** Close the AudioContext (double-close safe). */
  destroy(): void {
    if (this.ctx !== null && !this.closed) {
      this.closed = true;
      void this.ctx.close();
    }
    this.ctx = null;
    this.master = null;
    this.noiseBuffer = null;
  }

  // ── Internals ──────────────────────────────────────────────────────

  /**
   * Lazy AudioContext + master gain init. Returns null when Web Audio is
   * unavailable (jsdom), construction threw, or destroy() already ran.
   */
  private ensureContext(): AudioContext | null {
    if (this.ctx !== null) return this.ctx;
    if (this.initFailed || this.closed) return null;
    try {
      if (typeof window === 'undefined') {
        this.initFailed = true;
        return null;
      }
      const Ctor =
        window.AudioContext ??
        (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (Ctor === undefined) {
        this.initFailed = true;
        return null;
      }
      const ctx = new Ctor();
      const master = ctx.createGain();
      master.gain.value = this.muted ? 0 : MASTER_GAIN;
      master.connect(ctx.destination);
      this.ctx = ctx;
      this.master = master;
      return ctx;
    } catch {
      this.initFailed = true;
      return null;
    }
  }

  /**
   * Common per-effect gate: lazy init, mute early-out, 45ms rate limit.
   * Returns the AudioContext to schedule on, or null to no-op.
   */
  private begin(effect: string): AudioContext | null {
    if (this.muted) return null;
    const ctx = this.ensureContext();
    if (ctx === null || this.master === null) return null;
    const now = Date.now();
    const last = this.lastPlayedAt[effect] ?? -Infinity;
    if (now - last < MIN_RETRIGGER_MS) return null;
    this.lastPlayedAt[effect] = now;
    return ctx;
  }

  /** Attack + exponential-decay envelope gain node, pre-wired to master. */
  private envelope(ctx: AudioContext, at: number, dur: number, peak: number): GainNode {
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(ENV_FLOOR, at);
    gain.gain.linearRampToValueAtTime(peak, at + ATTACK_S);
    // Never exponential-ramp to 0 — floor at 0.0001, then hard-zero.
    gain.gain.exponentialRampToValueAtTime(ENV_FLOOR, at + dur);
    gain.gain.setValueAtTime(0, at + dur);
    if (this.master !== null) gain.connect(this.master);
    return gain;
  }

  /** One enveloped oscillator voice, optional exponential pitch sweep. */
  private tone(ctx: AudioContext, opts: ToneOpts): void {
    const osc = ctx.createOscillator();
    osc.type = opts.type;
    osc.frequency.setValueAtTime(opts.freq, opts.at);
    if (opts.freqEnd !== undefined) {
      osc.frequency.exponentialRampToValueAtTime(opts.freqEnd, opts.at + opts.dur);
    }
    osc.connect(this.envelope(ctx, opts.at, opts.dur, opts.peak));
    osc.start(opts.at);
    osc.stop(opts.at + opts.dur + 0.01);
  }

  /** One enveloped noise voice through a biquad, optional filter sweep. */
  private noise(ctx: AudioContext, opts: NoiseOpts): void {
    const src = ctx.createBufferSource();
    src.buffer = this.ensureNoiseBuffer(ctx);
    src.loop = true; // buffer is 1s; loop covers any dur without edge clicks
    const filter = ctx.createBiquadFilter();
    filter.type = opts.filterType;
    filter.frequency.setValueAtTime(opts.freq, opts.at);
    if (opts.freqEnd !== undefined) {
      filter.frequency.exponentialRampToValueAtTime(opts.freqEnd, opts.at + opts.dur);
    }
    if (opts.q !== undefined) filter.Q.setValueAtTime(opts.q, opts.at);
    src.connect(filter);
    filter.connect(this.envelope(ctx, opts.at, opts.dur, opts.peak));
    src.start(opts.at);
    src.stop(opts.at + opts.dur + 0.01);
  }

  /** Shared 1s white-noise buffer, created once per context. */
  private ensureNoiseBuffer(ctx: AudioContext): AudioBuffer {
    if (this.noiseBuffer !== null) return this.noiseBuffer;
    const length = Math.floor(ctx.sampleRate * NOISE_BUFFER_SECONDS);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    this.noiseBuffer = buffer;
    return buffer;
  }

  /** Shared body of shipExplosion/death: low noise boom + sub thump. */
  private explosion(dur: number): void {
    const ctx = this.ctx;
    if (ctx === null) return;
    const t = ctx.currentTime;
    // Low rumble: noise through a lowpass sweeping down to ~200Hz and below.
    this.noise(ctx, {
      filterType: 'lowpass',
      freq: 400,
      freqEnd: 80,
      at: t,
      dur,
      peak: 0.3,
    });
    // Sub-oscillator thump: sine dropping into the sub range.
    this.tone(ctx, {
      type: 'sine',
      freq: 90,
      freqEnd: 40,
      at: t,
      dur: Math.min(dur, 0.3),
      peak: 0.3,
    });
  }
}
